import React, { useState } from "react";
import axios from "axios";
import { API, clearToken } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  User, Globe, Bell, Shield, Trash2, Check, Pencil, X,
  Camera, Download, AlertTriangle, Sun, Moon, Sparkles, LogOut
} from "lucide-react";
import { toast } from "sonner";
import { AvatarDisplay, AvatarPicker, FITNESS_GOALS } from "./SettingsWidgets";
import { devError } from "../utils/devLogger";
import { useTheme } from "../hooks/useTheme";

const DarkModeSettingsButton = () => {
  const { isDark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      className="w-10 h-10 rounded-xl flex items-center justify-center bg-sky-100 dark:bg-slate-700 hover:bg-sky-200 dark:hover:bg-slate-600 transition-colors"
      data-testid="dark-mode-settings-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark
        ? <Sun className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
        : <Moon className="w-5 h-5 text-sky-600" strokeWidth={1.5} />}
    </button>
  );
};

export const ProfileSection = ({
  profile, setProfile, avatars, t,
  showAvatarPicker, setShowAvatarPicker,
  onPhotoUpload, onAvatarSelect, onSetFitnessGoal
}) => {
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(profile?.name || "");
  const [editingBio, setEditingBio] = useState(false);
  const [newBio, setNewBio] = useState(profile?.bio || "");

  const saveName = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.put(`${API}/settings/profile`, { name: newName.trim() });
      setProfile(res.data.profile);
      setEditingName(false);
      toast.success(t("settings.profileUpdated"));
    } catch {
      toast.error("Failed to update name");
    }
  };

  const saveBio = async () => {
    try {
      const res = await axios.put(`${API}/settings/profile`, { bio: newBio.trim() });
      setProfile(res.data.profile);
      setEditingBio(false);
      toast.success(t("settings.profileUpdated"));
    } catch {
      toast.error("Failed to update bio");
    }
  };

  return (
    <div className="glass-card p-6" data-testid="profile-section">
      <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
        <User className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
        {t("settings.profile")}
      </h3>

      <div className="flex items-start gap-5 mb-6">
        <div className="relative group">
          <AvatarDisplay profile={profile} />
          <button
            onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
            data-testid="change-avatar-btn"
          >
            <Camera className="w-6 h-6 text-white" />
          </button>
        </div>
        <div className="flex-1 space-y-2">
          {editingName ? (
            <div className="flex items-center gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="flex-1" data-testid="name-input" onKeyDown={(e) => e.key === "Enter" && saveName()} />
              <Button size="icon" onClick={saveName} className="glow-button" data-testid="save-name-btn"><Check className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => { setEditingName(false); setNewName(profile?.name || ""); }}><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h4 className="text-xl font-medium text-sky-900">{profile?.name}</h4>
              <Button size="icon" variant="ghost" onClick={() => setEditingName(true)} className="text-sky-500" data-testid="edit-name-btn"><Pencil className="w-4 h-4" /></Button>
            </div>
          )}
          <p className="text-sm text-slate-500">{profile?.email}</p>
          {editingBio ? (
            <div className="flex items-center gap-2 mt-2">
              <Input value={newBio} onChange={(e) => setNewBio(e.target.value)} placeholder="What are you focusing on?" maxLength={200} className="flex-1 text-sm" data-testid="bio-input" onKeyDown={(e) => e.key === "Enter" && saveBio()} />
              <Button size="icon" onClick={saveBio} className="glow-button"><Check className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => { setEditingBio(false); setNewBio(profile?.bio || ""); }}><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-1 mt-1">
              <p className="text-sm text-slate-500 italic">{profile?.bio || "Add a bio or status..."}</p>
              <Button size="icon" variant="ghost" onClick={() => setEditingBio(true)} className="text-sky-400 h-6 w-6" data-testid="edit-bio-btn"><Pencil className="w-3 h-3" /></Button>
            </div>
          )}
        </div>
      </div>

      {showAvatarPicker && (
        <AvatarPicker avatars={avatars} profile={profile} onSelect={onAvatarSelect} onUpload={onPhotoUpload} onCancel={() => setShowAvatarPicker(false)} />
      )}

      <div className="mt-4">
        <Label className="text-sky-800 text-sm font-medium mb-2 block">Fitness Goal</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FITNESS_GOALS.map((goal) => {
            const GoalIcon = goal.icon;
            const isActive = profile?.fitness_goal === goal.id;
            return (
              <button key={goal.id} onClick={() => onSetFitnessGoal(goal.id)}
                className={`flex items-center gap-2 p-2.5 rounded-xl transition-all text-left text-sm ${isActive ? "bg-sky-100 border-2 border-sky-400 text-sky-700 shadow-sm" : "bg-white/50 border-2 border-transparent hover:bg-white/80 text-slate-600"}`}
                data-testid={`goal-${goal.id}`}>
                <GoalIcon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-sky-500" : "text-slate-400"}`} strokeWidth={1.5} />
                <span className="text-xs sm:text-sm">{goal.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const LanguageSection = ({ languages, currentLang, onLanguageSync, t }) => (
  <div className="glass-card p-6" data-testid="language-section">
    <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
      <Globe className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
      {t("settings.language")}
    </h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {languages.map((lang) => (
        <button key={lang.code} onClick={() => onLanguageSync(lang.code)}
          className={`flex items-center gap-2 p-3 rounded-xl transition-all border-2 ${currentLang === lang.code ? "border-sky-400 bg-sky-50 shadow-md" : "border-transparent bg-white/50 hover:bg-white/80"}`}
          data-testid={`lang-${lang.code}`}>
          <span className="text-lg">{lang.flag}</span>
          <span className={`text-sm ${currentLang === lang.code ? "font-medium text-sky-700" : "text-slate-600"}`}>{lang.label}</span>
          {currentLang === lang.code && <Check className="w-4 h-4 text-sky-500 ml-auto" />}
        </button>
      ))}
    </div>
  </div>
);

export const NotificationsSection = ({ settings, updateSetting, pushSupported, pushSubscribed, onSubscribe, onUnsubscribe, t }) => (
  <div className="glass-card p-6" data-testid="notifications-section">
    <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
      <Bell className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
      {t("settings.notifications")}
    </h3>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sky-800">{t("settings.dailyReminder")}</Label>
          <p className="text-sm text-slate-500">{t("settings.dailyReminderDesc")}</p>
        </div>
        <Switch checked={settings?.daily_reminder ?? true} onCheckedChange={(v) => updateSetting("daily_reminder", v)} data-testid="daily-reminder-toggle" />
      </div>
      {settings?.daily_reminder && (
        <div className="flex items-center justify-between">
          <Label className="text-sky-800">{t("settings.reminderTime")}</Label>
          <Input type="time" value={settings?.reminder_time || "09:00"} onChange={(e) => updateSetting("reminder_time", e.target.value)} className="w-32" data-testid="reminder-time-input" />
        </div>
      )}
      {pushSupported && (
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sky-800">{t("settings.pushNotifications")}</Label>
            <p className="text-sm text-slate-500">{t("settings.pushNotificationsDesc")}</p>
          </div>
          <Switch checked={pushSubscribed} onCheckedChange={(v) => v ? onSubscribe() : onUnsubscribe()} data-testid="push-toggle" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sky-800">{t("settings.notificationSound")}</Label>
          <p className="text-sm text-slate-500">{t("settings.notificationSoundDesc")}</p>
        </div>
        <Switch checked={settings?.notification_sound ?? true} onCheckedChange={(v) => updateSetting("notification_sound", v)} data-testid="sound-toggle" />
      </div>
    </div>
  </div>
);

export const SmartNudgesSection = ({ notifPrefs, updateNotifPref }) => (
  <div className="glass-card p-6" data-testid="smart-nudges-section">
    <h3 className="text-lg font-medium text-sky-800 mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
      <Sparkles className="w-5 h-5 text-violet-500" strokeWidth={1.5} />
      Smart Nudges
    </h3>
    <p className="text-xs text-slate-400 mb-4">
      {notifPrefs.paused ? "Nudges are paused. Toggle any option below to re-enable." : "Contextual reminders that adapt to your activity."}
    </p>
    <div className="space-y-4">
      {[
        { key: "daily_nudges", label: "Daily check-in nudges", desc: "Remind you to complete your questionnaire" },
        { key: "streak_reminders", label: "Streak reminders", desc: "Celebrate milestones and protect your streak" },
        { key: "achievement_alerts", label: "Achievement alerts", desc: "Get notified when you earn badges" },
        { key: "subscription_nudges", label: "Subscription reminders", desc: "Heads up before your plan expires" },
      ].map(item => (
        <div key={item.key} className="flex items-center justify-between">
          <div>
            <Label className="text-sky-800">{item.label}</Label>
            <p className="text-sm text-slate-500">{item.desc}</p>
          </div>
          <Switch checked={notifPrefs[item.key] ?? true} onCheckedChange={(v) => updateNotifPref(item.key, v)} data-testid={`nudge-${item.key.replace(/_/g, '-')}-toggle`} />
        </div>
      ))}
      <div className="flex items-center justify-between border-t border-sky-100 pt-4">
        <div>
          <Label className="text-sky-800">Weekly AI digest email</Label>
          <p className="text-sm text-slate-500">Sunday summary of your progress with AI insights</p>
        </div>
        <Switch checked={notifPrefs.weekly_digest_email ?? true} onCheckedChange={(v) => updateNotifPref("weekly_digest_email", v)} data-testid="nudge-digest-toggle" />
      </div>
    </div>
  </div>
);

export const DisplaySection = ({ settings, updateSetting, t }) => (
  <div className="glass-card p-6" data-testid="display-section">
    <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
      <Shield className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
      {t("settings.display")}
    </h3>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sky-800 dark:text-sky-200">{t("settings.darkMode")}</Label>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("settings.darkModeDesc") || "Toggle dark theme"}</p>
        </div>
        <DarkModeSettingsButton />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sky-800">{t("settings.showStreaks")}</Label>
          <p className="text-sm text-slate-500">{t("settings.showStreaksDesc")}</p>
        </div>
        <Switch checked={settings?.show_streaks ?? true} onCheckedChange={(v) => updateSetting("show_streaks", v)} data-testid="streaks-toggle" />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sky-800">{t("settings.showAchievements")}</Label>
          <p className="text-sm text-slate-500">{t("settings.showAchievementsDesc")}</p>
        </div>
        <Switch checked={settings?.show_achievements ?? true} onCheckedChange={(v) => updateSetting("show_achievements", v)} data-testid="achievements-toggle" />
      </div>
    </div>
  </div>
);

export const DataPrivacySection = ({ t, navigate }) => (
  <div className="glass-card p-6" data-testid="gdpr-section">
    <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
      <Download className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
      {t("settings.dataPrivacy") || "Data & Privacy"}
    </h3>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sky-800">{t("settings.exportData") || "Export My Data"}</Label>
          <p className="text-sm text-slate-500">{t("settings.exportDataDesc") || "Download all your personal data as JSON (GDPR)"}</p>
        </div>
        <Button variant="outline" onClick={async () => {
          try {
            const res = await axios.get(`${API}/gdpr/export`);
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url;
            a.download = `lumifit_data_export_${new Date().toISOString().split("T")[0]}.json`;
            a.click(); URL.revokeObjectURL(url);
            toast.success(t("settings.exportSuccess") || "Data exported successfully!");
          } catch (err) {
            devError("Data export failed:", err);
            toast.error("Failed to export data");
          }
        }} className="text-sky-600 border-sky-200" data-testid="export-data-btn">
          <Download className="w-4 h-4 mr-2" />
          {t("settings.download") || "Download"}
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-red-600">{t("settings.eraseData") || "Erase All Data"}</Label>
          <p className="text-sm text-slate-500">{t("settings.eraseDataDesc") || "Permanently delete all your data (GDPR Right to Erasure)"}</p>
        </div>
        <Button variant="outline" onClick={async () => {
          if (!window.confirm("Are you sure? This will permanently delete ALL your data and cannot be undone.")) return;
          try {
            await axios.delete(`${API}/gdpr/delete`);
            toast.success("All data erased. Logging out...");
            clearToken(); navigate("/");
          } catch { toast.error("Failed to erase data"); }
        }} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="erase-data-btn">
          <AlertTriangle className="w-4 h-4 mr-2" />
          {t("settings.erase") || "Erase"}
        </Button>
      </div>
    </div>
  </div>
);

export const AccountActionsSection = ({ onLogout, onDelete, showDeleteConfirm, setShowDeleteConfirm, t }) => (
  <div className="glass-card p-6" data-testid="account-section">
    <div className="flex flex-col gap-3">
      <Button variant="outline" onClick={onLogout} className="w-full justify-start text-sky-700" data-testid="logout-btn">
        <LogOut className="w-5 h-5 mr-2" strokeWidth={1.5} />
        {t("settings.logout")}
      </Button>
      {!showDeleteConfirm ? (
        <Button variant="outline" onClick={() => setShowDeleteConfirm(true)} className="w-full justify-start text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-account-btn">
          <Trash2 className="w-5 h-5 mr-2" strokeWidth={1.5} />
          {t("settings.deleteAccount")}
        </Button>
      ) : (
        <div className="p-4 bg-red-50 rounded-xl border border-red-200">
          <p className="text-sm text-red-700 mb-3">{t("settings.deleteConfirm")}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={onDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">{t("settings.confirmDelete")}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  </div>
);
